import React, { ReactNode } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Table from 'react-bootstrap/Table';
import classNames from 'classnames';
import Tooltip from 'react-bootstrap/Tooltip';
import './styles.scss';
import { SortDirection } from 'utils/types';

export type FixedHeader = {
  header: string;
  sortable?: {
    current: boolean;
    direction: string;
    onSort: any;
  };
};

type VariableHeader = {
  value: string;
  tooltip?: ReactNode;
};

export type FixedRow = {
  value: string;
  tooltip?: ReactNode;
};

interface Props {
  headers: {
    fixed: Array<FixedHeader>;
    variable: Array<VariableHeader>;
  };
  topHeaders?: [
    {
      headerLines: Array<string>;
      tooltip: ReactNode;
    }
  ];
  rows: [
    {
      id: string;
      onRowClick(arg: any): void;
      fixed: Array<FixedRow>;
      variable: [
        {
          value: string;
          color: string;
          tooltip?: ReactNode;
        }
      ];
    }
  ];
}

const OpportunityMatrixTable: React.FC<Props> = ({ headers, rows, topHeaders = null }) => {
  return (
    <div className="opportunity-matrix">
      <div className="opportunity-matrix-table">
        <div className="fixed-table-container">
          <Table className="fixed-table">
            <colgroup>
              {headers.fixed.map((item, index) => (
                <col key={index} />
              ))}
            </colgroup>
            <tbody>
              {topHeaders !== null && (
                <tr>
                  {headers.fixed.map((item, index) => (
                    <td key={index} />
                  ))}
                </tr>
              )}
              <tr className="header-row">
                {headers.fixed.map((item, index) => {
                  if (item.sortable) {
                    const headerClasses = classNames({
                      sortable: true,
                      asc: item.sortable.direction === SortDirection.ASC && item.sortable.current,
                      desc: item.sortable.direction === SortDirection.DESC && item.sortable.current
                    });
                    return (
                      <td key={index} className={headerClasses} onClick={item.sortable.onSort}>
                        {item.header}
                      </td>
                    );
                  }
                  return <td key={index}>{item.header}</td>;
                })}
              </tr>
              {rows.map((row, index) => (
                <tr
                  key={index}
                  className={row.onRowClick !== undefined ? 'clickable-row' : null}
                  onClick={row.onRowClick}
                >
                  {row.fixed.map((item, index) => (
                    <td key={index}>
                      {item.tooltip ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id="tooltip">{item.tooltip}</Tooltip>}
                        >
                          <span>{item.value}</span>
                        </OverlayTrigger>
                      ) : (
                        <div>{item.value}</div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="value-table-container">
          <Table>
            <tbody>
              {topHeaders !== null && (
                <tr>
                  {topHeaders.map((topHeader, index) => (
                    <td key={index}>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="tooltip">{topHeader.tooltip}</Tooltip>}
                      >
                        <div>
                          {topHeader.headerLines.map((line, index) => (
                            <div key={index}>{line}</div>
                          ))}
                        </div>
                      </OverlayTrigger>
                    </td>
                  ))}
                </tr>
              )}
              <tr className="header-row">
                {headers.variable.map((header, index) => (
                  <td key={index}>
                    {header.tooltip ? (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="tooltip">{header.tooltip}</Tooltip>}
                      >
                        <div>{header.value}</div>
                      </OverlayTrigger>
                    ) : (
                      <div>{header.value}</div>
                    )}
                  </td>
                ))}
              </tr>
              {rows.map((row, index) => (
                <tr key={index}>
                  {row.variable.map((item, index) => {
                    const dateClass = classNames({
                      'date-cell': true,
                      red: item.color === 'red',
                      yellow: item.color === 'yellow',
                      green: item.color === 'green',
                      purple: item.color === 'purple'
                    });
                    return (
                      <td className={dateClass} key={index}>
                        {item.tooltip ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip">{item.tooltip}</Tooltip>}
                          >
                            <div>{item.value}</div>
                          </OverlayTrigger>
                        ) : (
                          <div>{item.value}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default OpportunityMatrixTable;
